import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MagnifyingGlass, Phone, Funnel, Star } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CatalogPage() {
  const { user, activeOrgId } = useAuth();
  const showWsp = user?.role === "master_admin";
  const [products, setProducts] = useState([]);
  const [types, setTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [userAllowed, setUserAllowed] = useState(null);
  const [userAllAccess, setUserAllAccess] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [favIds, setFavIds] = useState(new Set());

  useEffect(() => {
    axios.get(`${API}/products/types`, { withCredentials: true })
      .then(r => setTypes(r.data.types))
      .catch(() => {});
    axios.get(`${API}/categories`, { withCredentials: true })
      .then(r => setCategories(r.data.categories || []))
      .catch(() => {});
    axios.get(`${API}/favourites`, { withCredentials: true })
      .then(r => setFavIds(new Set(r.data.favourites?.map(f => f.product_id) || [])))
      .catch(() => {});
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (selectedType !== "all") params.append("type", selectedType);
      if (selectedCategory !== "all") params.append("category", selectedCategory);
      if (search) params.append("search", search);
      if (activeOrgId) params.append("org_id", activeOrgId);
      const { data } = await axios.get(`${API}/products?${params}`, { withCredentials: true });
      setProducts(data.products);
      setTotalPages(data.pages);
      setTotal(data.total);
      setUserAllowed(data.user_allowed_categories);
      setUserAllAccess(data.user_all_categories !== false);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, selectedType, selectedCategory, search, activeOrgId]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  useEffect(() => { setPage(1); }, [search, selectedType, selectedCategory]);

  const toggleFav = async (productId) => {
    if (favIds.has(productId)) {
      await axios.delete(`${API}/favourites/${productId}`, { withCredentials: true });
      setFavIds(prev => { const n = new Set(prev); n.delete(productId); return n; });
      toast.success("Removed from favourites");
    } else {
      await axios.post(`${API}/favourites/${productId}`, {}, { withCredentials: true });
      setFavIds(prev => new Set(prev).add(productId));
      toast.success("Added to favourites");
    }
  };

  // Categories visible as chips: scoped to user's allowed when partner_store/practitioner
  const visibleCategoryChips = (() => {
    if (userAllAccess || !userAllowed) return categories;
    const allowed = new Set(userAllowed);
    return categories.filter(c => allowed.has(c.key));
  })();

  const catLabel = (key) => categories.find(c => c.key === key)?.label || key;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="catalog-page">
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">PRODUCT CATALOG</p>
        <h1 className="font-[Chivo] text-3xl sm:text-4xl font-black tracking-tighter text-zinc-950 mt-1">
          Price List
        </h1>
        <p className="text-sm text-zinc-500 mt-1">{total} products available</p>
      </div>

      {/* Category Chips */}
      {visibleCategoryChips.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="category-chips">
          <button
            onClick={() => setSelectedCategory("all")}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border transition-colors ${selectedCategory === "all" ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-950"}`}
            data-testid="cat-chip-all"
          >
            All Categories
          </button>
          {visibleCategoryChips.map(c => (
            <button
              key={c.key}
              onClick={() => setSelectedCategory(c.key)}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border transition-colors ${selectedCategory === c.key ? "bg-[#0055FF] text-white border-[#0055FF]" : "bg-white text-zinc-600 border-zinc-300 hover:border-[#0055FF]"}`}
              data-testid={`cat-chip-${c.key}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6" data-testid="catalog-filters">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            placeholder="Search by ingredient, type, or strength..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10 rounded-none"
            data-testid="catalog-search-input"
          />
        </div>
        <div className="w-full sm:w-56">
          <Select value={selectedType} onValueChange={setSelectedType}>
            <SelectTrigger className="rounded-none" data-testid="catalog-type-filter">
              <Funnel size={14} className="mr-2 text-zinc-400" />
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {types.map(t => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Products Table */}
      <div className="border border-zinc-200 overflow-x-auto" data-testid="catalog-table">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500 w-10"></th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Type</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Ingredient</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Categories</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Strength</th>
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">Qty</th>
              {showWsp && <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">WSP</th>}
              <th className="border-b-2 border-zinc-950 py-3 px-4 font-mono text-xs uppercase tracking-wider text-zinc-500">{showWsp ? "RRP" : "Price"}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={showWsp ? 8 : 7} className="py-12 text-center text-sm text-zinc-500 font-mono">LOADING...</td></tr>
            ) : products.length === 0 ? (
              <tr><td colSpan={showWsp ? 8 : 7} className="py-12 text-center text-sm text-zinc-500">No products found</td></tr>
            ) : (
              products.map((p, i) => (
                <tr key={p.id || i} className={`border-b border-zinc-200 transition-colors ${p.restricted ? "bg-zinc-50/60 opacity-60" : "hover:bg-zinc-50"}`} data-testid={`product-row-${i}`}>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => toggleFav(p.id)}
                      disabled={p.restricted}
                      className="text-zinc-400 hover:text-amber-500 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid={`star-${p.id}`}
                      title={p.restricted ? "Not available for your account type" : ""}
                    >
                      <Star size={16} weight={favIds.has(p.id) ? "fill" : "regular"} />
                    </button>
                  </td>
                  <td className="py-3 px-4 text-sm text-zinc-500">{p.type}</td>
                  <td className="py-3 px-4 text-sm text-zinc-800 font-medium">{p.ingredient}</td>
                  <td className="py-3 px-4">
                    <div className="flex flex-wrap gap-1">
                      {(p.categories || []).map(k => (
                        <span key={k} className="inline-block px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-zinc-100 text-zinc-600 border border-zinc-200" data-testid={`cat-tag-${p.id}-${k}`}>
                          {catLabel(k)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-zinc-600 font-mono">{p.strength}</td>
                  <td className="py-3 px-4 text-sm text-zinc-600">{p.qty}</td>
                  {showWsp && (
                    <td className="py-3 px-4 text-sm font-semibold">
                      {p.quote_required ? (
                        <span className="inline-flex items-center gap-1 text-amber-600"><Phone size={12} />Quote</span>
                      ) : p.wsp != null ? `$${p.wsp.toFixed(2)}` : "-"}
                    </td>
                  )}
                  <td className="py-3 px-4 text-sm font-semibold">
                    {p.price_hidden ? (
                      <span className="text-zinc-400 text-xs font-normal" data-testid={`price-hidden-${i}`}>Contact for pricing</span>
                    ) : p.quote_required ? (
                      <span className="inline-flex items-center gap-1 text-amber-600"><Phone size={12} />Contact for Price</span>
                    ) : p.rrp != null ? `$${p.rrp.toFixed(2)}` : "-"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4" data-testid="catalog-pagination">
          <p className="text-sm text-zinc-500">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 text-sm border border-zinc-200 text-zinc-600 hover:bg-zinc-50 disabled:opacity-50 transition-colors"
              data-testid="catalog-prev-page"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 text-sm border border-zinc-200 text-zinc-600 hover:bg-zinc-50 disabled:opacity-50 transition-colors"
              data-testid="catalog-next-page"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Delivery Fees Info */}
      <div className="mt-8 border border-zinc-200 p-6 bg-zinc-50" data-testid="delivery-fees-info">
        <h3 className="font-[Chivo] font-bold text-zinc-950 mb-3">Delivery Fees</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">EXPRESS POST</p>
            <p className="font-semibold text-zinc-950 mt-1">$12.00</p>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">OWN COURIER</p>
            <p className="font-semibold text-zinc-950 mt-1">Free</p>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">URGENT + EXPRESS</p>
            <p className="font-semibold text-zinc-950 mt-1">$32.00</p>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">URGENT + COURIER</p>
            <p className="font-semibold text-zinc-950 mt-1">$20.00</p>
          </div>
        </div>
      </div>
    </div>
  );
}
